import { differenceInDays } from 'date-fns';
import * as Sentry from '@sentry/browser';

export function asyncImport(promise) {
  // this ensures we don't have any stale chunks
  return promise.catch(e => {
    Sentry.captureException(e);
    Sentry.captureMessage('Missing chunk caused page reload');
    window.location.reload();
  });
}

export function displayName(count = 0, displayNamePlural = 'Items', displayNameSingular = 'Item') {
  return count === 1 ? displayNameSingular : displayNamePlural;
}

export function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function parseApiDateString(date) {
  const split = date.split('-');
  const year = parseInt(split[0]);
  const month = parseInt(split[1]);
  const day = parseInt(split[2]);

  return {
    date: new Date(year, month - 1, day),
    year: year,
    month: month,
    day: day,
  };
}

export function collectAssetsFromAssetAssociations(assetAssociations, store) {
  const assets = [];
  const collect = (assetAssociations, assets) => {
    assetAssociations.forEach(aa => {
      if (aa.assetBType === 'Topic') {
        assets.push({ type: 'Topic', id: aa.assetBId });
      } else if (aa.assetBType === 'V3Flashcard') {
        assets.push({ type: 'V3Flashcard', id: aa.assetBId });
      } else if (aa.assetBType === 'Grouping') {
        const childGrouping = store.getters['groupings/all'][aa.assetBId];
        if (childGrouping) {
          collect(childGrouping.assetAssociations, assets);
        }
      }
    });
    return assets;
  };
  return collect(assetAssociations, assets);
}

export function titleFromAsset(asset, type) {
  if (!asset || !type) return '';
  const lowerCasedType = type.toLowerCase();
  if (asset) {
    switch (lowerCasedType) {
      case 'v3flashcard':
        return asset.question && stripHtml(asset.question);
      case 'grouping':
        const items = asset.assetAssociations.length;
        return `${asset.name}: ${items} Items`;
      case 'attachment':
      case 'topic':
        return asset.assetTitle;
      default:
        return asset.title && stripHtml(asset.title);
    }
  }
}

export function legacyFormLink(store) {
  let url = `https://${store.getters['app/legacyUrl']}`;

  if (process.env.API_PORT) {
    url += `:${process.env.API_PORT}`;
  }

  return url += '/cart/add';
}

export function removePhonePrefix(phone) {
  return /^\+1/.test(phone) ? phone.substring(2) : phone;
}

export function interpolatePhone(phone) {
  if (phone.length < 3) {
    return phone;
  } else if (phone.length >= 3 && phone.length < 6) {
    return `${phone.slice(0,3)}-${phone.slice(3,6)}`;
  } else if (phone.length >= 6) {
    return `${phone.slice(0,3)}-${phone.slice(3,6)}-${phone.slice(6)}`;
  }
}

export function userShouldSeeShareModal(store) {
  let pvar = store.getters['productVars/get']['homeShareMessage'].value;
  if (!pvar) return;
  pvar = JSON.parse(pvar);
  const user = store.getters['session/currentUser'];
  const now = new Date();
  const lastSeen = user.lastShownShareMessage ? new Date(user.lastShownShareMessage) : false;
  const signUpDate = new Date(user.createdAt);

  const afterSignupDelay = differenceInDays(now, signUpDate) >= pvar.message_delay;
  const timeAfterLastSeen = lastSeen ? differenceInDays(now, lastSeen) >= pvar.message_interval : true;

  return afterSignupDelay && timeAfterLastSeen;
}

export function formatCompletionTime(timeInMinutes) {
  const hours = Math.floor(timeInMinutes / 60);
  const minutes = timeInMinutes % 60;
  if (hours > 0 || minutes > 0) {
    let base = 'Approx. ';
    const joiner = ' and ';
    if (hours > 0) {
      base += `${hours} ${hours > 1 ? 'hrs' : 'hr'}`;
      if (minutes > 0) {
        base += joiner;
      }
    }
    if (minutes > 0) {
      base += `${minutes} ${minutes > 1 ? 'mins' : 'min'}`;
    }
    return base;
  }
}

export function formatTotalSeconds(timeInSeconds) {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  if (minutes < 1) {
    return `${Math.floor(timeInSeconds)}s`;
  }
  if (minutes > 0 || seconds > 0) {
    let base = '';
    if (minutes > 0) {
      base += `${minutes}m `;
    }
    if (seconds > 0) {
      base += `${seconds}s`;
    }
    return base;
  }
}

export function parseMustacheTemplate(template, translationTable) {
  const matcher = /{{{?(#[a-z]+ )?[a-z]+.[a-z]*}?}}/g;
  const matches = template.match(matcher);
  if (!matches) {
    return template;
  }

  const matchValues = matches.map(match => {
    const value = match.replace(/[{}]\S/g, '').trim();
    return {
      match: match,
      value: translationTable[value],
    };
  });
  let modifiableTemplate = template;
  matchValues.forEach(match => {
    modifiableTemplate = modifiableTemplate.replace(match.match, match.value);
  });
  return modifiableTemplate;
}

export const assetTypeGetterMap = {
  'Attachment': 'attachments/get',
  'Deck': 'decks/get',
  'Grouping': 'groupings/all',
  'Topic': 'topics/topics',
  'V3Flashcard': 'flashcards/flashcards',
};

// This is pretty much the same as the asset type getter map except that the
// only keys here are purchasable meaning they are added to purchase orders
const purchasableTypeGetterMap = {
  'appDashboard': 'dashboards/get',
  'attachment': 'attachments/get',
  'category': 'categories/categories',
  'learningModule': 'learningModules/get',
  'navigationItem': 'navigationItems/navigationItems',
};

export function determineUpsellModal(params, getters) {
  let mutationName = 'setUpsellModal';
  const baseClassification = getters['productVars/get']['tiered_subscription_classification_id'];
  const user = getters['user/get'];
  const classification = getters['session/classification'];
  if (baseClassification && baseClassification.parsedValue && baseClassification.parsedValue === user.classificationId) {
    // if the preferred PO is set on the user's current classification show the regular upsell model
    // which sound trigger the preferred PO modal.
    if (!classification || !classification.preferredPurchaseOrderId) {
      mutationName = 'setBaseUpsellModal';
    }
  }
  let catalogPageId;
  if (params.show && (params.resource || params.feature)) {
    const userCatalogEnabled = user?.catalogPricingEnabled;
    if (userCatalogEnabled) {
      const rootPurchaseOrders = getters['purchaseOrders/rootAddon'];
      if (rootPurchaseOrders.length > 0) {
        let includedInPOs = [];
        if (params.feature) {
          rootPurchaseOrders.forEach(po => {
            const included = po.features?.includes(params.feature);
            if (included) {
              includedInPOs.push(po.id);
            }
          });
        } else {
          const resource = getters[purchasableTypeGetterMap[params.resource.type]]?.[params.resource.id];
          if (resource) {
            rootPurchaseOrders.forEach(po => {
              const included = resource?.unlockedBy?.includes(po.id);
              if (included) {
                includedInPOs.push(po.id);
              }
            });
          }
        }

        const page = getters['catalog/pageList'].find(page => {
          const canPurchasePage = user?.previewer ? true : page.purchaseEnabled;
          return canPurchasePage && page.visibility && includedInPOs.includes(page.purchaseOrderId);
        });

        if (page) {
          mutationName = 'setCatalogModal';
          catalogPageId = page.id;
        }
      }
    }
  }
  return {
    mutationName,
    catalogPageId,
  };
}

export function rateLimitingEnabled({ store, apptimize, analytics }) {
  const apptimizeEnabled = apptimize?.checkExperiments({

    experimentName: 'Rate Limiting',
    bool: 'rateLimit',
    defaultVal: null,
    matcher: /Rate Limiting/,
    analytics: analytics,
  });
  if (apptimizeEnabled !== null && apptimizeEnabled !== undefined) {
    return apptimizeEnabled;
  }

  const userEnabled = store.getters['user/rateLimitingEnabled'];
  return userEnabled;
}

export function changeRootPTagToSpan(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const body = doc.body;
  if (body.children.length === 1 && body.firstElementChild.tagName === 'P') {
    const div = doc.createElement('span');
    div.innerHTML = body.firstElementChild.innerHTML;
    body.replaceChild(div, body.firstElementChild);
  }

  const serializer = new XMLSerializer();
  const newHtmlString = serializer.serializeToString(doc.body);

  return newHtmlString;
}

export function snakeToPascal(snakeStr) {
  return snakeStr
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}
